/**
 * Resume Data Model
 * 
 * Each experience and project contains content variants that respond to mixer controls:
 * - technical: Implementation details, architecture, code
 * - business: Outcomes, metrics, impact
 * - ic: Individual contributor perspective (execution, craft)
 * - leadership: Ownership, decision-making, stakeholders
 * - consulting: Speed, ambiguity, client relationships
 * - enterprise: Scale, compliance, process
 */

export const profile = {
  name: "Tom Keech",
  contact: {
    phone: "(226) 377-6136",
    email: "thomas.v.keech@gmail.com",
    github: "github.com/mot667",
    linkedin: "linkedin.com/in/tomkeech/",
    devpost: "devpost.com/mot667",
    website: "tomkeech.com"
  },
  education: [
    {
      school: "Ivey Business School",
      degree: "Master of Business Administration (MBA)",
      period: "Expected March 2027",
      current: true
    },
    {
      school: "Western University",
      degree: "Bachelor in Software Engineering BESc",
      period: "September 2017 - 2022",
      current: false
    }
  ],
  languages: ["English", "French (Fluent)"],
  summary: {
    technical: "Full-stack engineer with deep expertise in React, Node.js, Spring Boot, and cloud architecture. Passionate about building performant, scalable systems.",
    business: "Technology leader who bridges engineering excellence with business outcomes. Track record of delivering products that reach 80,000+ users.",
    ic: "Hands-on developer who ships production code daily. Strong craft in frontend and backend systems, with attention to code quality and user experience.",
    leadership: "Engineering leader who drives product direction, mentors teams, and navigates complex stakeholder relationships. Thrives at the intersection of technology and strategy."
  }
};

export const skills = [
  { name: "JavaScript", level: 95 },
  { name: "React", level: 90 },
  { name: "Node.js", level: 85 },
  { name: "SQL", level: 80 },
  { name: "Python", level: 75 },
  { name: "React Native", level: 85 },
  { name: "C++/C#", level: 70 },
  { name: "Java", level: 75 }
];

export const businessSkills = [
  { name: "Product Thinking", category: "strategy" },
  { name: "Stakeholder Communication", category: "leadership" },
  { name: "Requirements Discovery", category: "consulting" },
  { name: "Strategic Framing", category: "strategy" },
  { name: "Tradeoff Analysis", category: "strategy" },
  { name: "Client Management", category: "consulting" },
  { name: "Technical Scoping", category: "consulting" },
  { name: "French (Fluent)", category: "language" }
];

export const experiences = [
  {
    id: "rbc-senior",
    title: "Senior Software Developer",
    company: "RBC",
    team: "Digital Workplace Solutions",
    period: "May 2022 - Present",
    type: "enterprise",
    
    // Content variants for Technical ↔ Business slider
    description: {
      technical: "Architected and developed internal applications using React, Node.js, and Spring Boot. Implemented microservices architecture with focus on performance and maintainability. Built a fully on-prem Translation service with custom NLP pipeline.",
      business: "Led development of enterprise tools used by all RBC employees globally. Translation service deployment improved cross-team collaboration efficiency by 40%. Reduced time-to-market for internal tools through streamlined development processes."
    },
    
    // Content variants for IC ↔ Leadership slider
    role: {
      ic: "Wrote production code across the full stack. Debugged complex issues, optimized database queries, and maintained high code coverage. Participated in code reviews and technical documentation.",
      leadership: "Owned product direction for multiple internal applications from ideation to production. Coordinated with cross-functional teams, presented to stakeholders, and made architectural decisions that scaled across the organization."
    },
    
    // Content variants for Consulting ↔ Enterprise
    context: {
      consulting: "Navigated ambiguous requirements by rapidly prototyping solutions and iterating based on user feedback. Delivered MVPs quickly to validate assumptions.",
      enterprise: "Operated within RBC's security and compliance frameworks. Implemented solutions that met regulatory requirements while maintaining developer velocity. Managed SCRUM ceremonies across multiple concurrent projects."
    },
    
    highlights: [
      "Production applications serving RBC employees globally",
      "On-prem Translation service architecture",
      "Jira/SCRUM process optimization"
    ],
    
    technologies: ["React", "Node.js", "Spring Boot", "Java", "SQL Server"]
  },
  
  {
    id: "security-research",
    title: "Independent Security Researcher",
    company: "HackerOne",
    team: "Bug Bounty Program",
    period: "2021 - Present",
    type: "consulting",
    
    description: {
      technical: "Identified and responsibly disclosed security vulnerabilities in web applications. Conducted manual penetration testing, analyzed authentication flows, and discovered critical bugs in production systems.",
      business: "Successfully reported vulnerabilities to major technology companies through HackerOne's coordinated disclosure platform. Demonstrated security awareness and attention to detail in identifying risks before malicious exploitation."
    },
    
    role: {
      ic: "Performed independent security assessments, documented findings with clear reproduction steps, and collaborated with security teams on remediation timelines.",
      leadership: "Contributed to improved security posture for large organizations. Maintained professional communication throughout disclosure processes and built reputation within the security community."
    },
    
    context: {
      consulting: "Applied systematic approach to identifying vulnerabilities across different technology stacks. Prioritized findings based on potential business impact.",
      enterprise: "Worked within formal bug bounty programs with established rules of engagement. Understood enterprise security considerations and compliance requirements."
    },
    
    highlights: [
      "Accepted vulnerabilities at major tech companies",
      "Responsible disclosure via HackerOne",
      "Security-first development mindset"
    ],
    
    technologies: ["Web Security", "Penetration Testing", "OWASP", "Burp Suite"]
  },
  
  {
    id: "rbc-fullstack",
    title: "Full Stack Developer",
    company: "RBC",
    team: "Digital Workplace Solutions",
    period: "Sept 2021 - May 2022",
    type: "enterprise",
    
    description: {
      technical: "Built full-stack features using Spring Boot, Java, Microsoft SQL Server, and React. Implemented RESTful APIs and optimized database performance for high-traffic internal tools.",
      business: "Delivered critical workplace solutions that improved employee productivity. Established demo cadence that aligned stakeholder expectations with engineering capacity."
    },
    
    role: {
      ic: "Developed features end-to-end, from database schema design to React component implementation. Focused on writing clean, testable code.",
      leadership: "Organized weekly progress meetings and product demos. Communicated technical constraints to business stakeholders and proposed alternatives."
    },
    
    context: {
      consulting: "Balanced multiple priorities and delivered incremental value each sprint despite shifting requirements.",
      enterprise: "Worked within established enterprise architecture patterns. Integrated with existing systems while maintaining security standards."
    },
    
    highlights: [
      "Spring Boot microservices development",
      "Weekly stakeholder demos",
      "Database optimization"
    ],
    
    technologies: ["Spring Boot", "Java", "SQL Server", "React"]
  },
  
  {
    id: "rbc-amplify",
    title: "Software Developer",
    company: "RBC",
    team: "RBC Amplify",
    period: "May 2021 - Sept 2021",
    type: "enterprise",
    
    description: {
      technical: "Built cross-platform application using React Native, React, PostgreSQL, Python, and Flask. Implemented real-time features and mobile-first responsive design.",
      business: "Developed solution for organizing hybrid teams, addressing RBC's future-of-work challenge. Application selected for rollout to 80,000+ RBC employees."
    },
    
    role: {
      ic: "Wrote mobile and web code in a fast-paced team of four. Iterated rapidly on features based on user testing and executive feedback.",
      leadership: "Pitched solution continuously to high-level RBC executives throughout summer program. Won Crowd Favorite & Best Team at AMP Expo."
    },
    
    context: {
      consulting: "Operated in startup-like conditions within enterprise. Validated assumptions quickly, pivoted based on feedback, and delivered under tight timeline.",
      enterprise: "Solved a real enterprise challenge (hybrid work coordination) that required understanding organizational dynamics and compliance needs."
    },
    
    highlights: [
      "80,000+ user rollout",
      "Crowd Favorite & Best Team award",
      "Executive-level pitching"
    ],
    
    technologies: ["React Native", "React", "PostgreSQL", "Python", "Flask"]
  },
  
  {
    id: "vantage-point",
    title: "Software Consultant",
    company: "Vantage Point Consulting",
    period: "Jan 2020 - Present",
    type: "consulting",
    
    description: {
      technical: "Developed custom software solutions including e-commerce platforms, inventory systems, and API integrations. Implemented Shopify customizations with real-time data sync.",
      business: "Consulted with clients to identify software needs, scoped projects, and delivered tailored solutions. Enabled direct-to-consumer sales channels for retail clients."
    },
    
    role: {
      ic: "Designed and implemented solutions independently. Made all technical decisions from architecture to deployment.",
      leadership: "Managed client relationships end-to-end. Negotiated pricing, set timelines, and ensured successful delivery across multiple concurrent projects."
    },
    
    context: {
      consulting: "Assessed diverse client needs, refined ambiguous requirements into actionable scopes. Balanced speed of delivery with solution quality. Navigated budget constraints creatively.",
      enterprise: "Implemented solutions that integrated with enterprise systems (Shopify, Firebase) while maintaining data integrity and security."
    },
    
    highlights: [
      "End-to-end client management",
      "Custom e-commerce solutions",
      "Real-time inventory systems"
    ],
    
    technologies: ["React", "Node.js", "Shopify API", "Firebase", "JavaScript"]
  }
];

export const projects = [
  {
    id: "wufers",
    title: "Wufers E-commerce Platform",
    client: "Bosco and Roxie's/Wufers",
    period: "October 2024 - Present",
    type: "consulting",
    
    description: {
      technical: "Built custom Shopify e-commerce site with 'Build Your Own Box' feature. Implemented point-based product selection system with real-time cart calculations.",
      business: "Launched direct-to-consumer sales channel for pet treat company. Enabled personalized purchasing experience that increased average order value."
    },
    
    role: {
      ic: "Developed custom Shopify theme and JavaScript logic for box builder. Integrated with Shopify's cart API for seamless checkout.",
      leadership: "Scoped project requirements with client, proposed feature set, and delivered on timeline."
    },
    
    context: {
      consulting: "Delivered quickly to meet client's launch timeline. Prioritized features that maximized business impact within budget constraints.",
      enterprise: "Built on Shopify's enterprise e-commerce platform with proper security and PCI compliance."
    },
    
    technologies: ["Shopify", "JavaScript", "Liquid", "CSS"]
  },
  
  {
    id: "519-inventory",
    title: "519 Inventory System",
    client: "Five One Nine Clothing",
    period: "August 2023",
    type: "consulting",
    
    description: {
      technical: "Developed website using HTML, JS, CSS, and Firebase. Integrated Shopify API for real-time inventory updates post-purchase. Implemented custom SKU logic for accurate stock management.",
      business: "Created live inventory tracking system with reorder thresholds. Eliminated manual inventory reconciliation and prevented stockouts."
    },
    
    role: {
      ic: "Built complete system from scratch including Firebase integration, Shopify webhooks, and responsive frontend.",
      leadership: "Identified client pain points and proposed inventory solution that addressed root cause of operational inefficiencies."
    },
    
    context: {
      consulting: "Delivered full solution within tight timeline. Balanced feature scope with budget to maximize ROI for client.",
      enterprise: "Integrated with Shopify's enterprise ecosystem while maintaining data consistency across systems."
    },
    
    technologies: ["HTML", "JavaScript", "CSS", "Firebase", "Shopify API"]
  },
  
  {
    id: "mask-reminder",
    title: "Mask-Reminder",
    period: "September 2020",
    type: "personal",
    
    description: {
      technical: "Built React Native application using geofencing to trigger location-based push notifications. Implemented background location services with battery optimization.",
      business: "Solved real-world problem during COVID-19 pandemic. Published to Google Play Store with 25+ downloads."
    },
    
    role: {
      ic: "Designed, developed, and published complete mobile application independently. Managed entire product lifecycle from idea to app store.",
      leadership: "Identified user need and shipped solution to production. Made all product and technical decisions."
    },
    
    context: {
      consulting: "Moved quickly from idea to published app. Prioritized essential features for initial release.",
      enterprise: "Navigated Google Play Store requirements and Android permission systems."
    },
    
    technologies: ["React Native", "Geofencing", "Push Notifications"]
  },
  
  {
    id: "wattz",
    title: "Wattz",
    event: "HackWestern 6",
    period: "November 2019",
    type: "hackathon",
    
    description: {
      technical: "Built React Native application displaying live analytics from foot pressure sensors connected via Arduino. Implemented real-time data visualization and Firebase storage.",
      business: "Created fitness tracking tool that provides actionable insights on exercise form. Analyzed historical workout data to show progress over time."
    },
    
    role: {
      ic: "Developed mobile frontend and integrated with hardware sensors. Wrote data processing logic for pressure displacement analysis.",
      leadership: "Contributed to team direction and presented to hackathon judges."
    },
    
    context: {
      consulting: "Delivered complete solution in 36-hour hackathon. Made rapid technical decisions under time pressure.",
      enterprise: "Integrated hardware and software systems with reliable data pipeline."
    },
    
    technologies: ["React Native", "Arduino", "Firebase", "Pressure Sensors"]
  }
];

export const achievements = [
  { title: "RBC AMP Expo Crowd Favorite & Best Team", year: "2021" },
  { title: "Dean's Honor List, Fall/Winter 2020", year: "2020" },
  { title: "Enactus Hacks Winner - Most Impactful Social Project", year: "2020" },
  { title: "TWG/Deloitte Ideation Bootcamp", year: "2019" },
  { title: "Judges Pick - 2018 Engineering Design Showcase", year: "2018" },
  { title: "TWG/Deloitte Hackathon Winner", year: "2018" },
  { title: "Western Scholarship of Excellence", year: "2017" }
];

export const moments = [
  {
    id: "wufers-launch",
    title: "E-commerce Launch",
    subtitle: "Wufers",
    description: "Launched a custom Shopify platform with Build Your Own Box feature for direct-to-consumer sales.",
    year: "2024",
    category: "launch"
  },
  {
    id: "atlantic-crossing",
    title: "North Atlantic Crossing",
    subtitle: "Personal Challenge",
    description: "Sailed two weeks across the North Atlantic from Newfoundland to Portugal. Learned invaluable lessons about leadership, composure under pressure, and trust.",
    year: "2024",
    category: "milestone"
  },
  {
    id: "translation-launch",
    title: "Translation Service Launch",
    subtitle: "RBC Digital Workplace",
    description: "Led the deployment of an on-prem translation service that reached all RBC employees globally.",
    year: "2022",
    category: "impact"
  },
  {
    id: "graduation",
    title: "Software Engineering Degree",
    subtitle: "Western University",
    description: "Graduated with a BESc in Software Engineering after 5 years of combining academics with real-world experience.",
    year: "2022",
    category: "education"
  },
  {
    id: "amp-win",
    title: "Crowd Favorite",
    subtitle: "RBC Amplify 2021",
    description: "Pitched a hybrid work solution to RBC executives and won both Crowd Favorite and Best Team awards at the AMP Expo.",
    year: "2021",
    category: "recognition"
  },
  {
    id: "first-client",
    title: "First Consulting Client",
    subtitle: "Vantage Point",
    description: "Landed first consulting client and delivered a custom software solution end-to-end.",
    year: "2020",
    category: "milestone"
  }
];

// Personal interests - adds a human layer to the professional profile
export const interests = [
  {
    id: "photography",
    title: "Film Photography",
    description: "Capturing moments on 35mm film. There's something about the intentionality of limited exposures.",
    icon: "📷"
  },
  {
    id: "sailing",
    title: "Sailing",
    description: "From weekend races to crossing the North Atlantic. The ocean teaches patience and respect.",
    icon: "⛵"
  },
  {
    id: "fitness",
    title: "Fitness",
    description: "Consistent training as a form of discipline. The gym is where I reset and think clearly.",
    icon: "💪"
  }
];
